import React, { useEffect, useMemo, useState } from "react";
import {
  useUpdateAgentSite,
  useSetupAgentSiteSSL,
  useVerifyAgentSiteSSL,
  useReissueSSL,
  useDeleteAgentSiteSSL,
  useAgentCurrentSite,
} from "../../../hooks/useAiAgents";
import { useAgentSiteSocketUpdates } from "../../../hooks/useAgentSiteSocketUpdates";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import Modal from "../../ui/UIElements/Modal";
import {
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  RefreshCw,
  Trash2,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

const mapApiErrorMessages = (error, fallback) => {
  if (Array.isArray(error?.data?.result)) {
    return error.data.result;
  }

  if (typeof error?.data?.result === "string") {
    return [error.data.result];
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return [error.message];
  }

  return [fallback];
};

const normalizeSslStatus = (status) => {
  if (!status) return null;

  const normalizedStatus = status
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, "_");

  if (
    normalizedStatus === "pre_issued" ||
    normalizedStatus === "pendingvalidation"
  ) {
    return "pending_validation";
  }

  if (normalizedStatus === "active") {
    return "issued";
  }

  return normalizedStatus;
};

const normalizeSslProcessStatus = (status) => {
  if (!status) return null;

  return status
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, "_");
};

const isValidCustomDomain = (domainValue) => {
  if (!domainValue) return false;

  const normalizedDomain = domainValue.trim().toLowerCase();

  if (!normalizedDomain) return false;
  if (normalizedDomain.includes("@")) return false;
  if (normalizedDomain.startsWith("http://")) return false;
  if (normalizedDomain.startsWith("https://")) return false;
  if (normalizedDomain.includes("/")) return false;
  if (/\s/.test(normalizedDomain)) return false;

  const domainRegex =
    /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

  return domainRegex.test(normalizedDomain);
};

const getDisplayDnsRecordValue = (record) => {
  const recordType = record?.type?.toString()?.trim()?.toUpperCase?.();
  const rawValue = record?.value;

  if (typeof rawValue !== "string") {
    return rawValue;
  }

  const normalizedValue = rawValue.trim().toLowerCase().replace(/\.$/, "");

  const mappedCnameTargets = new Set([
    "cname.vercel-dns.com",
    "72eb0642bbb9ba57.vercel-dns-017.com",
  ]);
  const isVercelCnameTarget = mappedCnameTargets.has(normalizedValue);

  if (recordType === "CNAME" && isVercelCnameTarget) {
    return "cname.finitless.com";
  }

  return rawValue;
};

const AgentSitesSection = ({ agent, isAgentEnabled = true }) => {
  const { t } = useTranslation("aiAgents");
  const [isEditingDomain, setIsEditingDomain] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [actionErrors, setActionErrors] = useState([]);
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingDangerAction, setPendingDangerAction] = useState(null);

  const toast = useToast();

  const { data: currentSite } = useAgentCurrentSite(agent?.id);

  const site = currentSite || agent?.site || null;
  const liveAgent = useMemo(
    () => ({
      ...agent,
      site,
    }),
    [agent, site],
  );

  useAgentSiteSocketUpdates(liveAgent);

  const updateSiteMutation = useUpdateAgentSite();
  const setupSSLMutation = useSetupAgentSiteSSL();
  const verifySSLMutation = useVerifyAgentSiteSSL();
  const reissueSSLMutation = useReissueSSL();
  const deleteSSLMutation = useDeleteAgentSiteSSL();

  useEffect(() => {
    setDomainInput(site?.domain || "");
  }, [site?.domain]);

  const defaultSiteUrl = site?.temporal_domain
    ? `https://${site.temporal_domain}.finitless.ai`
    : null;

  const customDomainUrl = site?.domain ? `https://${site.domain}` : null;

  const normalizedProcessStatus = normalizeSslProcessStatus(
    site?.ssl_process_status,
  );
  const isProcessing = normalizedProcessStatus === "pending";
  const normalizedStatus = normalizeSslStatus(site?.ssl_status);
  const dnsRecords = site?.deployment_provider_settings?.dns_records || [];
  const isVercelProvider =
    !site?.deployment_provider ||
    site?.deployment_provider?.toLowerCase?.() === "vercel";

  const isAnyMutationPending =
    updateSiteMutation.isPending ||
    setupSSLMutation.isPending ||
    verifySSLMutation.isPending ||
    reissueSSLMutation.isPending ||
    deleteSSLMutation.isPending;

  const getSSLStatusInfo = () => {
    if (!site?.ssl_status) {
      return {
        icon: AlertCircle,
        color: "text-gray-400",
        bgColor: "bg-gray-500/20",
        borderColor: "border-gray-500/30",
        label: t("sites.ssl.labels.noSsl"),
        tooltip: t("sites.ssl.tooltips.noSsl"),
      };
    }

    switch (normalizedStatus) {
      case "issued":
        return {
          icon: CheckCircle2,
          color: "text-green-400",
          bgColor: "bg-green-500/20",
          borderColor: "border-green-500/30",
          label: t("sites.ssl.labels.issued"),
          tooltip: t("sites.ssl.tooltips.issued"),
        };
      case "pending_validation":
        return {
          icon: Clock,
          color: "text-yellow-400",
          bgColor: "bg-yellow-500/20",
          borderColor: "border-yellow-500/30",
          label: t("sites.ssl.labels.pendingValidation"),
          tooltip: isProcessing
            ? t("sites.ssl.tooltips.pendingValidationProcessing")
            : t("sites.ssl.tooltips.pendingValidation"),
        };
      case "error":
        return {
          icon: XCircle,
          color: "text-red-400",
          bgColor: "bg-red-500/20",
          borderColor: "border-red-500/30",
          label: t("sites.ssl.labels.error"),
          tooltip: site?.ssl_error_type
            ? t("sites.ssl.tooltips.errorWithType", {
                error: site.ssl_error_type,
              })
            : t("sites.ssl.tooltips.error"),
        };
      case "void":
        return {
          icon: AlertCircle,
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
          borderColor: "border-gray-500/30",
          label: t("sites.ssl.labels.void"),
          tooltip: t("sites.ssl.tooltips.void"),
        };
      default:
        return {
          icon: AlertCircle,
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
          borderColor: "border-gray-500/30",
          label: site.ssl_status,
          tooltip: t("sites.ssl.tooltips.unknown", {
            status: site.ssl_status,
          }),
        };
    }
  };

  const steps = [
    {
      id: "domain",
      label: t("sites.steps.domain"),
      description: t("sites.steps.domainDescription"),
      complete: Boolean(site?.domain),
    },
    {
      id: "dns",
      label: t("sites.steps.dns"),
      description: t("sites.steps.dnsDescription"),
      complete: dnsRecords.length > 0,
    },
    {
      id: "setup",
      label: t("sites.steps.setup"),
      description: t("sites.steps.setupDescription"),
      complete: normalizedStatus && normalizedStatus !== "void",
    },
    {
      id: "verify",
      label: t("sites.steps.verify"),
      description: t("sites.steps.verifyDescription"),
      complete: normalizedStatus === "issued",
    },
  ];

  const clearErrors = () => {
    setActionErrors([]);
  };

  const handleCancelEdit = () => {
    setDomainInput(site?.domain || "");
    setIsEditingDomain(false);
    clearErrors();
  };

  const handleSaveDomain = async () => {
    const trimmedDomain = domainInput.trim();

    if (!trimmedDomain) {
      toast.warning(t("sites.validation.domainRequiredMessage"), {
        title: t("sites.validation.domainRequiredTitle"),
      });
      return;
    }

    if (!isValidCustomDomain(trimmedDomain)) {
      toast.warning(t("sites.validation.domainInvalidMessage"), {
        title: t("sites.validation.domainInvalidTitle"),
      });
      return;
    }

    clearErrors();

    try {
      await updateSiteMutation.mutateAsync({
        agentId: agent.id,
        domain: trimmedDomain.toLowerCase(),
      });

      handleApiSuccess(t("sites.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      setIsEditingDomain(false);
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.updateErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.updateErrorTitle"),
        defaultMessage: t("sites.toasts.updateErrorMessage"),
      });
    }
  };

  const handleSetupSSL = async () => {
    clearErrors();

    try {
      await setupSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.setupSuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.setupErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.setupErrorTitle"),
        defaultMessage: t("sites.toasts.setupErrorMessage"),
      });
    }
  };

  const handleVerifySSL = async () => {
    clearErrors();

    try {
      await verifySSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.verifySuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.verifyErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.verifyErrorTitle"),
        defaultMessage: t("sites.toasts.verifyErrorMessage"),
      });
    }
  };

  const handleReissueSSL = async () => {
    clearErrors();

    try {
      await reissueSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.reissueSuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.reissueErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.reissueErrorTitle"),
        defaultMessage: t("sites.toasts.reissueErrorMessage"),
      });
    }
  };

  const handleDeleteDomain = async () => {
    clearErrors();

    try {
      await deleteSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.deleteSuccess"), toast, {
        title: t("common.success"),
      });
      setShowDeleteConfirmation(false);
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.deleteErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.deleteErrorTitle"),
        defaultMessage: t("sites.toasts.deleteErrorMessage"),
      });
    }
  };

  const closeDangerActionModal = () => {
    setPendingDangerAction(null);
  };

  const handleDangerActionConfirm = async () => {
    if (pendingDangerAction === "reissue") {
      await handleReissueSSL();
    }

    if (pendingDangerAction === "delete") {
      await handleDeleteDomain();
    }

    closeDangerActionModal();
  };

  const sslInfo = getSSLStatusInfo();
  const StatusIcon = sslInfo.icon;

  const canSetup =
    Boolean(site?.domain) &&
    !isProcessing &&
    normalizedStatus !== "pending_validation" &&
    normalizedStatus !== "issued";

  const canVerify =
    Boolean(site?.domain) &&
    !isProcessing &&
    (normalizedStatus === "pending_validation" || normalizedStatus === "error");

  const shouldShowSetupGuidance =
    Boolean(site?.domain) && normalizedStatus === "void" && !isProcessing;
  const shouldShowVerifyGuidance =
    Boolean(site?.domain) &&
    normalizedStatus === "pending_validation" &&
    !isProcessing;
  const shouldWarnIssuedDangerActions = normalizedStatus === "issued";

  const showSetupOnly = canSetup && !canVerify;
  const showVerifyOnly = canVerify && !canSetup;
  const showBothActions = canSetup && canVerify;

  const setupActionLabel =
    showSetupOnly && normalizedStatus === "void"
      ? t("sites.actions.setupSslAfterDns")
      : t("sites.actions.setupSsl");
  const dangerActionTitle =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.title")
      : t("sites.dangerActions.reissue.title");
  const dangerActionMessage =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.message")
      : t("sites.dangerActions.reissue.message");
  const dangerActionConfirmLabel =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.confirm")
      : t("sites.dangerActions.reissue.confirm");
  const dangerActionButtonClass =
    pendingDangerAction === "delete"
      ? "bg-red-700 hover:bg-red-800"
      : "bg-amber-600 hover:bg-amber-700";

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-6">
      <h3 className="mb-4 text-lg font-semibold text-white">
        {t("sites.title")}
      </h3>

      <div className="space-y-4">
        {defaultSiteUrl && (
          <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
            <div className="mb-1 text-sm text-gray-400">
              {t("sites.defaultSite")}
            </div>
            {isAgentEnabled ? (
              <a
                href={defaultSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 text-blue-400 hover:text-blue-300"
              >
                <span className="font-medium">{defaultSiteUrl}</span>
                <ExternalLink className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
            ) : (
              <div
                title={t("sites.tooltips.agentDisabledLink")}
                className="flex cursor-not-allowed items-center gap-2 text-gray-500"
              >
                <span className="font-medium">{defaultSiteUrl}</span>
                <ExternalLink className="h-4 w-4 opacity-50" />
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {t("sites.customDomain")}
            </div>
            {!isEditingDomain && (
              <button
                onClick={() => setIsEditingDomain(true)}
                className="text-sm text-purple-400 transition-colors hover:text-purple-300"
                disabled={isAnyMutationPending}
              >
                {customDomainUrl
                  ? t("sites.actions.change")
                  : t("sites.actions.addDomain")}
              </button>
            )}
          </div>

          {isEditingDomain ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-400">{t("sites.domainHint")}</p>
              <input
                type="text"
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                placeholder={t("sites.placeholder")}
                className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:ring-1 focus:ring-purple-500 focus:outline-none"
                disabled={isAnyMutationPending}
              />

              <div className="flex gap-2">
                <button
                  onClick={handleSaveDomain}
                  disabled={isAnyMutationPending}
                  className="flex-1 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-600"
                >
                  {updateSiteMutation.isPending
                    ? t("actions.saving")
                    : t("actions.save")}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isAnyMutationPending}
                  className="flex-1 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-600"
                >
                  {t("actions.cancel")}
                </button>
              </div>
            </div>
          ) : customDomainUrl ? (
            <div className="flex items-center justify-between gap-3">
              {isAgentEnabled ? (
                <a
                  href={customDomainUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 text-blue-400 hover:text-blue-300"
                >
                  <span className="font-medium">{site.domain}</span>
                  <ExternalLink className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </a>
              ) : (
                <div
                  title={t("sites.tooltips.agentDisabledLink")}
                  className="flex cursor-not-allowed items-center gap-2 text-gray-500"
                >
                  <span className="font-medium">{site.domain}</span>
                  <ExternalLink className="h-4 w-4 opacity-50" />
                </div>
              )}

              <div
                className={`flex items-center gap-2 rounded-full border px-2 py-1 ${sslInfo.bgColor} ${sslInfo.borderColor} ${sslInfo.color}`}
                title={sslInfo.tooltip}
              >
                <StatusIcon className="h-3 w-3" />
                <span className="text-xs font-medium">{sslInfo.label}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              {t("sites.empty.noCustomDomain")}
            </div>
          )}
        </div>

        {!isVercelProvider && (
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-300">
            {t("sites.providerNotSupported")}
          </div>
        )}

        {isVercelProvider && (
          <>
            <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
              <p className="mb-3 text-sm font-medium text-white">
                {t("sites.steps.title")}
              </p>
              <ul className="space-y-2">
                {steps.map((step, index) => (
                  <li key={step.id} className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${
                        step.complete
                          ? "bg-green-500/20 text-green-300"
                          : "bg-gray-700 text-gray-400"
                      }`}
                    >
                      {step.complete ? (
                        <ShieldCheck className="h-3 w-3" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-white">{step.label}</p>
                      <p className="text-xs text-gray-400">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {isProcessing && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-200">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("sites.processing")}</span>
              </div>
            )}

            {actionErrors.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-red-300">
                  <AlertCircle className="h-4 w-4" />
                  {t("sites.errors.title")}
                </div>
                <ul className="list-disc space-y-1 pl-5 text-sm text-red-200">
                  {actionErrors.map((errorMessage, index) => (
                    <li key={`${errorMessage}-${index}`}>{errorMessage}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">
                  {t("sites.dns.title")}
                </h4>
                <span className="text-xs text-gray-400">
                  {t("sites.dns.source")}
                </span>
              </div>

              {dnsRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 text-xs text-gray-400 uppercase">
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.type")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.name")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.value")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dnsRecords.map((record, index) => (
                        <tr
                          key={`${record.type}-${record.name}-${index}`}
                          className="border-b border-gray-800 last:border-0"
                        >
                          <td className="px-2 py-2 text-gray-200">
                            {record.type}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-200">
                            {record.name}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-200">
                            {getDisplayDnsRecordValue(record)}
                          </td>
                          <td className="px-2 py-2">
                            {record.verified ? (
                              <span className="inline-flex items-center gap-1 text-green-300">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {t("sites.dns.verified")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-yellow-300">
                                <Clock className="h-3.5 w-3.5" />
                                {t("sites.dns.pending")}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400">{t("sites.dns.empty")}</p>
              )}
            </div>

            {shouldShowSetupGuidance && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                <p className="text-sm font-semibold text-amber-200">
                  {t("sites.guidance.setupRequiredTitle")}
                </p>
                <p className="mt-1 text-sm text-amber-100">
                  {t("sites.guidance.setupRequiredDescription")}
                </p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-amber-100">
                  <li>{t("sites.guidance.setupRequiredSteps.addDns")}</li>
                  <li>{t("sites.guidance.setupRequiredSteps.clickSetup")}</li>
                </ol>
              </div>
            )}

            {shouldShowVerifyGuidance && (
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
                <p className="text-sm font-semibold text-blue-200">
                  {t("sites.guidance.verifyRequiredTitle")}
                </p>
                <p className="mt-1 text-sm text-blue-100">
                  {t("sites.guidance.verifyRequiredDescription")}
                </p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-blue-100">
                  <li>{t("sites.guidance.verifyRequiredSteps.confirmDns")}</li>
                  <li>{t("sites.guidance.verifyRequiredSteps.clickVerify")}</li>
                </ol>
              </div>
            )}

            {(showSetupOnly || showVerifyOnly || showBothActions) && (
              <div
                className={
                  showBothActions
                    ? "grid grid-cols-1 gap-2 md:grid-cols-2"
                    : "grid grid-cols-1 gap-2"
                }
              >
                {(showSetupOnly || showBothActions) && (
                  <button
                    onClick={handleSetupSSL}
                    disabled={!canSetup || isAnyMutationPending}
                    className="w-full rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-600"
                  >
                    {setupSSLMutation.isPending
                      ? t("sites.actions.settingUp")
                      : setupActionLabel}
                  </button>
                )}

                {(showVerifyOnly || showBothActions) && (
                  <button
                    onClick={handleVerifySSL}
                    disabled={!canVerify || isAnyMutationPending}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-600"
                  >
                    {verifySSLMutation.isPending
                      ? t("sites.actions.verifying")
                      : t("sites.actions.verifyDns")}
                  </button>
                )}
              </div>
            )}

            <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
              <button
                onClick={() => setShowAdvancedActions((current) => !current)}
                className="flex w-full items-center justify-between text-left text-sm font-medium text-gray-200"
                type="button"
              >
                <span>{t("sites.advanced.title")}</span>
                <RefreshCw
                  className={`h-4 w-4 ${showAdvancedActions ? "text-purple-300" : "text-gray-400"}`}
                />
              </button>

              {showAdvancedActions && (
                <div className="mt-3 space-y-3 border-t border-gray-700 pt-3">
                  <button
                    onClick={() => {
                      if (shouldWarnIssuedDangerActions) {
                        setPendingDangerAction("reissue");
                        return;
                      }

                      handleReissueSSL();
                    }}
                    disabled={
                      isProcessing || isAnyMutationPending || !site?.domain
                    }
                    className="w-full rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:bg-gray-600"
                  >
                    {reissueSSLMutation.isPending
                      ? t("sites.actions.reissuing")
                      : t("sites.actions.reissueSsl")}
                  </button>

                  {!showDeleteConfirmation ? (
                    <button
                      onClick={() => {
                        if (shouldWarnIssuedDangerActions) {
                          setPendingDangerAction("delete");
                          return;
                        }

                        setShowDeleteConfirmation(true);
                      }}
                      disabled={
                        isProcessing || isAnyMutationPending || !site?.domain
                      }
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-900/60 px-4 py-2 text-sm font-medium text-red-200 transition-colors hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("sites.actions.deleteDomain")}
                    </button>
                  ) : (
                    <div className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                      <p className="text-sm text-red-200">
                        {t("sites.deleteConfirm.message")}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDeleteDomain}
                          disabled={isAnyMutationPending}
                          className="flex-1 rounded-lg bg-red-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-gray-600"
                        >
                          {deleteSSLMutation.isPending
                            ? t("sites.actions.deleting")
                            : t("sites.actions.confirmDelete")}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirmation(false)}
                          disabled={isAnyMutationPending}
                          className="flex-1 rounded-lg bg-gray-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                        >
                          {t("actions.cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={Boolean(pendingDangerAction)}
        onClose={closeDangerActionModal}
        title={dangerActionTitle}
        subtitle={t("sites.dangerActions.subtitle")}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-300" />
              <div>
                <p className="text-sm font-medium text-amber-200">
                  {t("sites.dangerActions.warningTitle")}
                </p>
                <p className="mt-1 text-sm text-amber-100">
                  {dangerActionMessage}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={closeDangerActionModal}
              disabled={isAnyMutationPending}
              className="flex-1 rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:bg-gray-600"
            >
              {t("sites.dangerActions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleDangerActionConfirm}
              disabled={isAnyMutationPending}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-600 ${dangerActionButtonClass}`}
            >
              {isAnyMutationPending
                ? t("actions.saving")
                : dangerActionConfirmLabel}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentSitesSection;
